<template>
  <b-modal
    :id="modalId"
    :title="modalTitle"
    size="md"
    no-close-on-esc
    no-close-on-backdrop
    @hidden="onModalHidden"
  >
    <div v-if="alarm" class="mb-3">
      <b-form-group :label="$t('alarm.alarm_id')" class="text-capitalize mb-2">
        <div class="text-muted">{{ alarm.id }}</div>
      </b-form-group>
      <b-form-group :label="$t('unit.single')" class="text-capitalize mb-2">
        <div class="text-muted">{{ alarmName }}</div>
      </b-form-group>
      <b-form-group :label="$t('date.created_at')" class="text-capitalize mb-2">
        <div class="text-muted">{{ formattedDate }}</div>
      </b-form-group>
      <b-form-group :label="$t('alarm.status')" class="text-capitalize mb-2">
        <div class="text-muted">{{ alarmStatusLabel }}</div>
      </b-form-group>
    </div>

    <b-form>
      <b-form-group
        :label="$tc('users.title', 1) + ' (SOEID)'"
        label-for="alarm-user-select"
        class="text-capitalize"
      >
        <UsersSelectPaginated
          id="alarm-user-select"
          :value="form.user"
          :placeholder="$t('users.search_users')"
          :extra-params="usersSelectExtraParams"
          :show-email-in-label="false"
          @input="onUserInput"
        />
        <small v-if="errors.user" class="text-danger">{{ errors.user }}</small>
      </b-form-group>

      <b-form-group
        :label="$t('alarm.timeline.comment')"
        label-for="alarm-comment-input"
        class="text-capitalize"
      >
        <b-form-textarea
          id="alarm-comment-input"
          v-model="form.comment"
          :placeholder="$t('alarm.timeline.comment')"
          rows="4"
          max-rows="8"
        />
        <small v-if="errors.comment" class="text-danger">{{ errors.comment }}</small>
      </b-form-group>
    </b-form>

    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="$bvModal.hide(modalId)"
        :disabled="loading"
        class="text-capitalize"
      >
        {{ $t("actions.cancel") }}
      </b-button>
      <b-button
        variant="primary"
        @click="submitForm"
        :disabled="loading || !isFormValid"
        class="text-capitalize"
      >
        <b-spinner v-if="loading" small class="mr-50" />
        {{ $t("actions.send") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axios from "@axios";
import dateParser from "@/libs/date-parser";
import UsersSelectPaginated from "@/components/UsersSelectPaginated.vue";

export default {
  components: {
    UsersSelectPaginated,
  },
  props: {
    modalId: {
      type: String,
      default: "alarm-assign-task-modal",
    },
    alarm: {
      type: Object,
      default: null,
    },
    reopenMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      form: {
        user: null,
        comment: "",
      },
      errors: {
        user: null,
        comment: null,
      },
      usersSelectExtraParams: {
        withGroup: false,
        withPermissions: false,
        search_by: "name",
      },
    };
  },
  computed: {
    modalTitle() {
      return this.reopenMode
        ? this.$t("grid.slots.info.btns.reopen")
        : this.$t("unit.assign_task");
    },
    alarmName() {
      if (!this.alarm) return "";
      return this.alarm.unit ? this.alarm.unit.description : "";
    },
    formattedDate() {
      if (!this.alarm || !this.alarm.created) return "";
      return dateParser.parseDate(this.alarm.created);
    },
    alarmStatusLabel() {
      if (!this.alarm || !this.alarm.alarm_status) return "";
      return this.alarm.alarm_status;
    },
    isFormValid() {
      const hasUser = Boolean(this.form.user);
      const hasComment = Boolean(this.form.comment && this.form.comment.trim() !== "");
      return hasUser && hasComment;
    },
  },
  watch: {
    alarm: {
      handler() {
        this.form = { user: null, comment: "" };
        this.errors = { user: null, comment: null };
      },
      immediate: true,
    },
  },
  methods: {
    onUserInput(value) {
      this.form.user = value || null;
    },
    clearErrors() {
      this.errors = { user: null, comment: null };
    },
    validateForm() {
      this.clearErrors();
      let isValid = true;

      if (!this.form.user) {
        this.errors.user = this.$t("messages.validation.required", {
          field: this.$t("users.title"),
        });
        isValid = false;
      }

      if (!this.form.comment || this.form.comment.trim() === "") {
        this.errors.comment = this.$t("messages.validation.required", {
          field: this.$t("comment.title"),
        });
        isValid = false;
      }

      return isValid;
    },
    resetForm() {
      this.form = { user: null, comment: "" };
      this.clearErrors();
    },
    onModalHidden() {
      this.resetForm();
      this.$emit("hidden");
    },
    buildPayload() {
      const user = this.form.user;
      const idUser = user && (user.value !== undefined ? user.value : user.id);
      return {
        id_alarm: this.alarm.id,
        id_user: idUser,
        comment: this.form.comment.trim(),
      };
    },
    async submitForm() {
      if (!this.validateForm() || !this.alarm) {
        return;
      }

      this.loading = true;

      try {
        const payload = this.buildPayload();
        const response = await axios.post("v1/alarm_task_assign", payload);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("messages.success.alarm_reopened"),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        this.resetForm();
        this.$emit("task-assigned", response?.data);

        setTimeout(() => {
          this.$bvModal.hide(this.modalId);
        }, 500);
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title:
              error.response?.data?.message ||
              this.$t("messages.error.server_error"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.text-capitalize {
  text-transform: capitalize;
}
</style>
